/**
 * 1 つの KUMODeck アカウント（2026-10-07 console/one-account・旗 oneAccount）の英語。en.ts が `...enOneAccount` で取り込む
 * （ja.oneAccount.ts が同じキーを全部持つことを型で強制）。
 * 決め事（指示書）: 普通の人が読んで迷わない文。何が起きるか・次に何をすればよいかを具体的に。{product} は productName.ts が入れる（直書きしない）。
 * 画面: pages/AccountSignIn.tsx（ログイン・戻り）・components/ModeSwitch.tsx（切り替え）・pages/PlayerPage.tsx・pages/CreatorStart.tsx
 */
export const enOneAccount = {
  // ログイン（account へ行く）
  'oneAccount.signIn.title': 'Sign in',
  'oneAccount.signIn.lead': 'Sign in with your {product} account. The same account works for creating and for the apps you sign in to.',
  'oneAccount.signIn.button': 'Continue to sign in',
  'oneAccount.signIn.busy': 'Opening…',
  'oneAccount.signIn.storageBlocked': 'Your browser did not let this page save a sign-in step. Turn off private browsing or allow site data for this page, then try again.',
  'oneAccount.signIn.oldPasskeyTitle': 'Saved a passkey on this page before?',
  'oneAccount.signIn.oldPasskeyLead': 'You can still use it here for now.',
  // 戻り（/signin/callback）
  'oneAccount.callback.working': 'Signing you in…',
  'oneAccount.callback.failedTitle': 'Sign-in did not finish',
  'oneAccount.callback.expired': 'The sign-in step expired or was already used. Please sign in again.',
  'oneAccount.callback.otherTab': 'Sign-in was started in another tab or window, or this page was reloaded. Please sign in again here.',
  'oneAccount.callback.again': 'Sign in again',
  // 切り替え（上のバー）
  'oneAccount.mode.label': 'Switch view',
  'oneAccount.mode.player': 'Player',
  'oneAccount.mode.creator': 'Creator',
  // 前のログイン用パスキー（設計 §2）
  'oneAccount.oldPasskey.title': 'Please add your passkey again',
  'oneAccount.oldPasskey.body':
    'You signed in with a passkey saved for this page. Sign-in now happens on your account page, and this passkey does not work there. Add a new passkey once on your account page.',
  'oneAccount.oldPasskey.open': 'Open account page',
  'oneAccount.oldPasskey.later': 'Later',
  'oneAccount.openAccountPage': 'Open account page',
  // Player の画面
  'player.tabs.label': 'Account',
  'player.tabs.games': 'Games',
  'player.tabs.profile': 'Profile',
  'player.tabs.signIn': 'Sign-in',
  'player.games.next': 'Open a game made with {product} and press “Sign in with {product}”. You will be in with this account.',
  'player.title': 'Your account',
  'player.lead': 'The games you play with your {product} account.',
  'player.signInNeeded': 'Sign in again to see your account.',
  'player.email': 'Email',
  'player.noEmail': 'No email address yet',
  'player.name.label': 'Display name',
  'player.name.hint': 'Games can show this name. Up to 32 characters. Leave it empty to show no name.',
  'player.name.save': 'Save name',
  'player.name.saved': 'Saved.',
  'player.games.title': 'Connected games',
  'player.games.empty': 'You have not signed in to any game with this account yet.',
  'player.games.testVersion': 'Test version',
  'player.games.lastSignIn': 'Last signed in {date}',
  'player.games.notYet': 'Not signed in yet',
  'player.games.play': 'Play',
  'player.games.playTest': 'Play test version',
  'player.games.hide': 'Remove from this list',
  'player.games.hideHint': 'Remove from this list only hides the game here; it comes back the next time you sign in to it. Disconnect signs you out of that game on every device.',
  'player.games.disconnect': 'Disconnect',
  'player.games.disconnectConfirm': 'Disconnect {game}? You will be signed out of it on all your devices. Other games are not affected.',
  'player.methods.title': 'Ways to sign in',
  'player.methods.lead': 'You can sign in with any of these.',
  'player.methods.email': 'Email link or code',
  'player.methods.remove': 'Remove',
  'player.methods.removeConfirm': 'Remove {method}? You will not be able to sign in with it anymore.',
  'player.methods.add': 'Add a way to sign in',
  'player.methods.addHint': 'This opens your account page. When you are done, come back to this tab.',
  'player.delete.title': 'Delete account',
  'player.delete.lead': 'Your account and its connections to games are deleted, and you are signed out of every game. This cannot be undone.',
  'player.delete.button': 'Delete my account',
  'player.delete.confirm': 'Delete your account? This cannot be undone.',
  // 作り手でもある人（2026-10-08 core/one-account-3）: ボタンを出さず、作り手のアカウントの閉鎖（残高の確認）へ
  'player.delete.creatorLead': 'You also create with this account, so it cannot be deleted here. First close your creator account in Creator. It checks your balance before anything is removed.',
  'player.delete.creatorButton': 'Open creator account settings',
  'player.delete.closedLead': 'Your creator account is already closed. To delete this account too, please contact us.',
  'player.notYetHere': 'You cannot change this here yet. Please change it on your account page.',
  'error.last_method': 'This is your only way to sign in, so it cannot be removed. Add another way first.',
  'error.creator_account':
    'You also create with this account, so it cannot be deleted here. To stop creating, switch to Creator and close your creator account on the Account page first.',
  // 作り手を始める（招待コード）
  'creatorStart.title': 'Start creating',
  'creatorStart.leadRequired': 'Creating is invite-only for now. Enter the invite code you received.',
  'creatorStart.leadOptional': 'If you have an invite code, enter it. You can also leave it empty.',
  'creatorStart.submit': 'Start creating',
  'creatorStart.busy': 'Starting…',
  'creatorStart.emailRequired':
    'Your account has no email address yet. Creating needs one, for receipts and security notices. Add an email on your account page, then come back here and press the button again.',
  'creatorStart.emailInUse':
    'Another creator account already uses {email}, and we could not join the two automatically. To open your existing projects, sign out, then sign in the same way you did before when creating (for example Google, GitHub, or email and password), and connect your accounts there.',
  'creatorStart.signOut': 'Sign out',
  'creatorStart.already': 'This account can already create. Sign in again to open Creator.'
} as const;
